/**
 * The YouTube settings of one post (YouTube Data API, `videos.insert`).
 *
 * The YouTube API Services Terms and the audit of the app bind every upload:
 * - the posting page shows the channel that gets the video (name and avatar);
 * - the user picks the privacy status; there is NO default. Every status can be picked: the
 *   upload sends the status that the user chose (owner finding 2026-10-10: YouTube publishes
 *   public videos of Music24 before the API audit). YouTube decides the final state; the daily
 *   read and the publish poll read it;
 * - the user says if the video is made for kids (`selfDeclaredMadeForKids`); there is NO default;
 * - only the privacy status that the user chose can be stored for the channel ("Posting defaults",
 *   `campaignLaunch.ts`): it is the selected value of the next form. "Made for kids" is never
 *   stored and never selected: the user answers it for each campaign and for each single post
 *   (owner decision 2026-10-11);
 * - the page links the YouTube Terms of Service and the Google Privacy Policy and says that
 *   Music24 uses YouTube API Services.
 *
 * The web app, the server and the tests read this file. iOS and Android carry a copy of the rules
 * (`YouTubeSettingsRules.swift`, `YouTubeSettingsRules.kt`); change the three together. No imports.
 */

/** The privacy statuses of YouTube, the widest first. */
export const YOUTUBE_PRIVACY_STATUSES = ['public', 'unlisted', 'private'] as const;

export type YouTubePrivacyStatus = (typeof YOUTUBE_PRIVACY_STATUSES)[number];

export function isYouTubePrivacyStatus(value: unknown): value is YouTubePrivacyStatus {
  return typeof value === 'string' && (YOUTUBE_PRIVACY_STATUSES as readonly string[]).includes(value);
}

/** The pages that the consent line links. */
export const YOUTUBE_TERMS_URL = 'https://www.youtube.com/t/terms';
export const GOOGLE_PRIVACY_URL = 'https://policies.google.com/privacy';
export const YOUTUBE_MADE_FOR_KIDS_URL = 'https://support.google.com/youtube/answer/9527654';

/** The settings that the user chose for one YouTube video. Stored on the post, sent at the publish. */
export interface YouTubePostSettings {
  privacyStatus: YouTubePrivacyStatus;
  /** True: the video is made for kids (`status.selfDeclaredMadeForKids`). */
  madeForKids: boolean;
}

/** The form state: both choices stay null until the user makes them (no default). */
export interface YouTubeSettingsDraft {
  privacyStatus: YouTubePrivacyStatus | null;
  madeForKids: boolean | null;
}

export const EMPTY_YOUTUBE_SETTINGS: YouTubeSettingsDraft = {
  privacyStatus: null,
  madeForKids: null,
};

/**
 * `GET /accounts/:accountId/promo/channels/youtube/posting-info`: 200 when the account has a
 * YouTube channel that posts through the API, else 404.
 * `auditedApp` is always true since 2026-10-10. It is kept so that a client from before that day
 * shows no "private until Google verifies Music24" note: that note was wrong.
 */
export interface YouTubePostingInfoAnswer {
  auditedApp: true;
}

/** The reasons that a YouTube post cannot go out with these settings. */
export type YouTubeSettingsIssue = 'privacyMissing' | 'madeForKidsMissing';

/** Every reason that stops the post, in the order the form shows them. Empty: the post can go. */
export function youtubeSettingsIssues(draft: YouTubeSettingsDraft): YouTubeSettingsIssue[] {
  const issues: YouTubeSettingsIssue[] = [];
  if (draft.privacyStatus === null) {
    issues.push('privacyMissing');
  }
  if (draft.madeForKids === null) {
    issues.push('madeForKidsMissing');
  }
  return issues;
}

/** The draft as stored settings, or null while a choice is missing. */
export function youtubeSettingsFromDraft(draft: YouTubeSettingsDraft): YouTubePostSettings | null {
  if (draft.privacyStatus === null || draft.madeForKids === null) {
    return null;
  }
  return { privacyStatus: draft.privacyStatus, madeForKids: draft.madeForKids };
}

/** Stored settings back into the form (an edit of a post that has them). */
export function youtubeDraftFromSettings(settings: YouTubePostSettings | null): YouTubeSettingsDraft {
  return settings === null ? { ...EMPTY_YOUTUBE_SETTINGS } : { ...settings };
}

/** Reads a stored value (the JSON column) back as settings, or null when it is not a full, valid set. */
export function parseYouTubePostSettings(value: unknown): YouTubePostSettings | null {
  if (value === null || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (!isYouTubePrivacyStatus(record.privacyStatus) || typeof record.madeForKids !== 'boolean') {
    return null;
  }
  return { privacyStatus: record.privacyStatus, madeForKids: record.madeForKids };
}
